import { defineStore } from 'pinia';
import axios from '@/api/config/axios';

const CHANNELS = Object.freeze(['helpdesk', 'dashboard']);

const WELCOME_BY_CHANNEL = Object.freeze({
    helpdesk: {
        id: 'welcome',
        role: 'assistant',
        content:
            'Olá, professor(a)! Sou o assistente da plataforma. Pode escrever sua dúvida como preferir — eu explico passo a passo com base na **documentação oficial**. Por onde começamos?',
        escalate: false,
        soft_escalate: false,
        sources: [],
        contact: null,
    },
    dashboard: {
        id: 'welcome',
        role: 'assistant',
        content:
            'Olá! Estou analisando os dados do painel para destacar o que importa hoje. Enquanto isso, fique à vontade para perguntar sobre a fila, as correções ou o seu ritmo de trabalho.',
        escalate: false,
        soft_escalate: false,
        sources: [],
        contact: null,
    },
});

function createChannelState(channel) {
    const welcome = WELCOME_BY_CHANNEL[channel] || WELCOME_BY_CHANNEL.helpdesk;
    return {
        messages: [{ ...welcome, id: `welcome-${channel}` }],
        loading: false,
        error: null,
        lastEscalate: false,
        lastContact: null,
    };
}

function normalizeChannel(channel) {
    const value = String(channel || 'helpdesk').toLowerCase();
    return CHANNELS.includes(value) ? value : 'helpdesk';
}

export const useSupportAssistantStore = defineStore('supportAssistantStore', {
    state: () => ({
        byChannel: {
            helpdesk: createChannelState('helpdesk'),
            dashboard: createChannelState('dashboard'),
        },
        /**
         * Dock flutuante do canal dashboard (persiste na SPA ao trocar de rota).
         * Não grava o texto do chat em localStorage (minimização LGPD).
         */
        dock: {
            enabled: false,
            open: false,
            dismissed: false,
            unread: false,
            hasUserSession: false,
        },
    }),

    getters: {
        /** Compat: Central de Ajuda continua usando o canal helpdesk por padrão. */
        messages: (state) => state.byChannel.helpdesk.messages,
        loading: (state) => state.byChannel.helpdesk.loading,
        isLoading: (state) => state.byChannel.helpdesk.loading,
        error: (state) => state.byChannel.helpdesk.error,
        lastEscalate: (state) => state.byChannel.helpdesk.lastEscalate,
        lastContact: (state) => state.byChannel.helpdesk.lastContact,
        conversationHistory: (state) =>
            state.byChannel.helpdesk.messages
                .filter(
                    (m) =>
                        !String(m.id || '').startsWith('welcome') &&
                        (m.role === 'user' || m.role === 'assistant')
                )
                .map((m) => ({ role: m.role, content: m.content })),

        dashboardHasUserSession(state) {
            if (state.dock.hasUserSession) return true;
            const msgs = state.byChannel.dashboard?.messages || [];
            return msgs.some((m) => m.role === 'user');
        },

        shouldShowDashboardDock(state) {
            return (
                !!state.dock.enabled &&
                !state.dock.dismissed &&
                (state.dock.hasUserSession ||
                    (state.byChannel.dashboard?.messages || []).some((m) => m.role === 'user'))
            );
        },
    },

    actions: {
        channelState(channel = 'helpdesk') {
            const key = normalizeChannel(channel);
            if (!this.byChannel[key]) {
                this.byChannel[key] = createChannelState(key);
            }
            return this.byChannel[key];
        },

        channelMessages(channel = 'helpdesk') {
            return this.channelState(channel).messages;
        },

        channelHistory(channel = 'helpdesk') {
            return this.channelState(channel)
                .messages.filter(
                    (m) =>
                        !String(m.id || '').startsWith('welcome') &&
                        (m.role === 'user' || m.role === 'assistant')
                )
                .map((m) => ({ role: m.role, content: m.content }));
        },

        resetChat(channel = 'helpdesk') {
            const key = normalizeChannel(channel);
            this.byChannel[key] = createChannelState(key);
            this.byChannel[key].messages[0].id = `welcome-${key}-${Date.now()}`;
            if (key === 'dashboard') {
                this.dismissDashboardDock();
                this.dock.hasUserSession = false;
            }
        },

        markDashboardUserSession() {
            this.dock.hasUserSession = true;
            this.dock.dismissed = false;
        },

        enableDashboardDock() {
            if (!this.dashboardHasUserSession) return false;
            this.dock.enabled = true;
            this.dock.dismissed = false;
            return true;
        },

        openDashboardDock() {
            this.dock.enabled = true;
            this.dock.open = true;
            this.dock.dismissed = false;
            this.dock.unread = false;
        },

        minimizeDashboardDock() {
            this.dock.open = false;
        },

        dismissDashboardDock() {
            this.dock.enabled = false;
            this.dock.open = false;
            this.dock.dismissed = true;
            this.dock.unread = false;
        },

        setDashboardDockUnread(value = true) {
            this.dock.unread = !!value;
        },

        /**
         * Atualiza a mensagem inicial do canal dashboard com valor proativo.
         * Só aplica se a conversa ainda não começou (só welcome).
         */
        applyDashboardWelcome(content, actions = []) {
            const text = String(content || '').trim();
            if (!text) return false;

            const bucket = this.channelState('dashboard');
            const onlyWelcome =
                bucket.messages.length === 1 &&
                String(bucket.messages[0]?.id || '').startsWith('welcome');

            if (!onlyWelcome) return false;

            bucket.messages = [
                {
                    id: `welcome-dashboard-${Date.now()}`,
                    role: 'assistant',
                    content: text,
                    escalate: false,
                    soft_escalate: false,
                    sources: [],
                    actions: Array.isArray(actions) ? actions : [],
                    contact: null,
                    proactive: true,
                },
            ];
            bucket.error = null;
            bucket.lastEscalate = false;
            bucket.lastContact = null;
            return true;
        },

        async sendMessage(text, options = {}) {
            const channel = normalizeChannel(options.channel);
            const bucket = this.channelState(channel);
            const message = String(text || '').trim();
            if (!message || bucket.loading) return null;

            bucket.error = null;
            bucket.loading = true;

            const userMsg = {
                id: `u-${channel}-${Date.now()}`,
                role: 'user',
                content: message,
            };
            bucket.messages.push(userMsg);

            if (channel === 'dashboard') {
                this.markDashboardUserSession();
            }

            const history = this.channelHistory(channel).slice(0, -1).slice(-12);

            try {
                const response = await axios.post('/api/v1/support-assistant/chat', {
                    message,
                    history,
                    channel,
                });

                const payload = response.data?.data || {};
                const assistantMsg = {
                    id: `a-${channel}-${Date.now()}`,
                    role: 'assistant',
                    content:
                        payload.reply ||
                        'Não consegui responder agora. Tente novamente ou fale com o suporte.',
                    escalate: !!payload.escalate,
                    soft_escalate: !!payload.soft_escalate,
                    sources: Array.isArray(payload.sources) ? payload.sources : [],
                    actions: Array.isArray(payload.actions) ? payload.actions : [],
                    contact: payload.contact || null,
                    confidence: payload.confidence ?? null,
                    channel: payload.channel || channel,
                };

                bucket.messages.push(assistantMsg);
                bucket.lastEscalate = !!(payload.escalate || payload.soft_escalate);
                bucket.lastContact = payload.contact || null;

                if (channel === 'dashboard' && this.dock.enabled && !this.dock.open) {
                    this.dock.unread = true;
                }

                return assistantMsg;
            } catch (error) {
                const status = error?.response?.status;
                let errorText = 'Não foi possível enviar sua mensagem. Tente novamente.';

                if (status === 401 || status === 403) {
                    errorText = 'Faça login para conversar com o assistente.';
                } else if (status === 429) {
                    errorText = 'Muitas mensagens em pouco tempo. Aguarde um momento e tente de novo.';
                } else if (error?.response?.data?.message) {
                    errorText = error.response.data.message;
                }

                bucket.error = errorText;
                bucket.messages.push({
                    id: `e-${channel}-${Date.now()}`,
                    role: 'assistant',
                    content: errorText,
                    escalate: true,
                    soft_escalate: true,
                    sources: [],
                    actions: [
                        {
                            id: 'contato_suporte',
                            label: 'Falar com suporte',
                            to: '/ajuda?tab=contato',
                            variant: 'secondary',
                        },
                    ],
                    contact: null,
                    isError: true,
                });
                bucket.lastEscalate = true;
                if (channel === 'dashboard' && this.dock.enabled && !this.dock.open) {
                    this.dock.unread = true;
                }
                throw error;
            } finally {
                bucket.loading = false;
            }
        },

        buildWhatsAppUrl(extraText = '', channel = 'helpdesk') {
            const bucket = this.channelState(channel);
            const phone = bucket.lastContact?.whatsapp || '5577981426377';
            const base =
                channel === 'dashboard'
                    ? 'Olá! Precisei de ajuda no assistente da home e gostaria de falar com o suporte.'
                    : 'Olá! Precisei de ajuda no assistente da Central de Ajuda e gostaria de falar com o suporte.';
            const text = [base, extraText].filter(Boolean).join('\n\n');
            return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
        },

        buildMailtoUrl(extraText = '', channel = 'helpdesk') {
            const bucket = this.channelState(channel);
            const email = bucket.lastContact?.email || 'descompliquesuacorrecao@gmail.com';
            const subject = encodeURIComponent(
                channel === 'dashboard' ? 'Suporte — Assistente da home' : 'Suporte — Central de Ajuda'
            );
            const body = encodeURIComponent(
                [
                    'Olá,',
                    '',
                    channel === 'dashboard'
                        ? 'Precisei de ajuda no assistente da home e gostaria de falar com o suporte.'
                        : 'Precisei de ajuda no assistente da Central de Ajuda e gostaria de falar com o suporte.',
                    '',
                    extraText || '',
                ].join('\n')
            );
            return `mailto:${email}?subject=${subject}&body=${body}`;
        },
    },
});
